import { useEffect, useRef, useState } from "react";
import { Link } from "@/lib/router-compat";
import Autoplay from "embla-carousel-autoplay";
import { ArrowRight, MapPin, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { formatINR } from "@/lib/format";

type ShowcaseListing = {
  id: string;
  title: string;
  category: string;
  cover_photo_url: string | null;
  price_per_ton: number;
  tonnage: number;
  hub: string;
  ai_benchmark_pct: number | null;
};

/** Ferrous / iron scrap grades shown in the primary showcase. */
export const IRON_SCRAP_CATEGORIES = [
  "HMS 1",
  "HMS 2",
  "Cast Iron",
  "Billets",
  "Prime Steel",
  "Turnings",
  "Shredded",
];

const shuffle = <T,>(arr: T[]): T[] => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i] as T; a[i] = a[j] as T; a[j] = tmp;
  }
  return a;
};

type Props = {
  /** "iron" = ferrous grades only, "other" = everything else (dolomite, plastics, future materials). */
  scope?: "iron" | "other";
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  compact?: boolean;
  /** Hide the whole section (instead of skeletons) while loading. */
  hideWhenEmpty?: boolean;
};

const LiveListingsShowcase = ({
  scope = "iron",
  eyebrow = "Fresh lots, just listed",
  heading = "Available right now",
  subheading = "Real yards, real stock. Check the price, check the photos, take the call.",
  compact = false,
}: Props) => {
  const isSecondary = scope === "other";
  const tight = isSecondary;
  const [listings, setListings] = useState<ShowcaseListing[] | null>(null);
  const autoplay = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const inList = `(${IRON_SCRAP_CATEGORIES.map((c) => `"${c}"`).join(",")})`;
      let query = supabase
        .from("listings")
        .select("id,title,category,price_per_ton,tonnage,hub,ai_benchmark_pct")
        .eq("status", "Awaiting Buyer");
      query =
        scope === "iron"
          ? query.in("category", IRON_SCRAP_CATEGORIES as never[])
          : query.not("category", "in", inList);

      const { data: meta, error } = await query
        .order("created_at", { ascending: false })
        .limit(24);
      if (cancelled) return;
      if (error || !meta) {
        setListings([]);
        return;
      }
      const chosen = shuffle(meta).slice(0, 8);

      const ids = chosen.map((r) => r.id);
      if (ids.length === 0) {
        setListings([]);
        return;
      }
      const { data: covers } = await supabase
        .from("listings")
        .select("id,cover_photo_url")
        .in("id", ids);
      if (cancelled) return;
      const coverById = new Map<string, string | null>(
        (covers ?? []).map((c) => [c.id as string, c.cover_photo_url ?? null]),
      );
      const withCovers = chosen.map(
        (row) =>
          ({ ...row, cover_photo_url: coverById.get(row.id) ?? null }) as ShowcaseListing,
      );
      setListings(withCovers);
    })();
    return () => {
      cancelled = true;
    };
  }, [scope]);

  // Loading
  if (listings === null) {
    if (scope === "other") return null;
    return (
      <section className="border-t border-border bg-background">
        <div className="container py-12 md:py-16">
          <div className="mb-8 max-w-2xl">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="mt-3 h-8 w-80" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-72 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Empty — hide gracefully
  if (listings.length === 0) return null;

  return (
    <section
      className={
        scope === "other"
          ? "bg-muted/30"
          : "border-t border-border bg-background"
      }
    >
      <div className={tight ? "container py-5 md:py-6" : compact ? "container py-8 md:py-10" : "container py-12 md:py-16"}>
        <div className={tight ? "flex flex-col gap-4 md:flex-row md:items-end md:justify-between" : "flex flex-col gap-6 md:flex-row md:items-end md:justify-between"}>
          <div className={tight ? "max-w-xl" : "max-w-2xl"}>
            <span className={tight ? "inline-flex items-center gap-2 rounded-full bg-success/15 px-2.5 py-1 text-[10px] font-semibold text-success" : "inline-flex items-center gap-2 rounded-full bg-success/15 px-3 py-1 text-xs font-semibold text-success"}>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
              </span>
              {eyebrow}
            </span>
            <h2
              className={
                tight
                  ? "mt-2 text-lg font-bold md:text-xl"
                  : compact
                    ? "mt-3 text-xl font-bold md:text-2xl"
                    : "mt-3 text-2xl font-bold md:text-3xl lg:text-4xl"
              }
            >
              {heading}
            </h2>
            <p className={tight ? "mt-1 text-xs text-muted-foreground" : "mt-2 text-sm text-muted-foreground md:text-base"}>
              {subheading}
            </p>
          </div>
          <Button asChild variant="outline" size={tight ? "sm" : "default"} className="self-start md:self-auto">
            <Link to="/buyer">
              Browse all lots <ArrowRight className={tight ? "ml-1 h-3 w-3" : "ml-1 h-4 w-4"} />
            </Link>
          </Button>
        </div>

        <Carousel
          opts={{ align: "start", loop: true }}
          plugins={[autoplay.current]}
          className={tight ? "mt-4" : compact ? "mt-6" : "mt-8"}
        >
          <CarouselContent className={tight ? "-ml-3" : "-ml-4"}>
            {listings.map((l) => {
              const img = l.cover_photo_url || null;
              return (
                <CarouselItem
                  key={l.id}
                  className={tight ? "pl-3 sm:basis-1/3 lg:basis-1/5" : "pl-4 sm:basis-1/2 lg:basis-1/4"}
                >
                  <Card className="h-full overflow-hidden transition-shadow hover:shadow-md">
                    <Link to="/buyer" className="block">
                      <div className={tight ? "aspect-[2/1] w-full overflow-hidden bg-muted" : compact ? "aspect-[16/10] w-full overflow-hidden bg-muted" : "aspect-[4/3] w-full overflow-hidden bg-muted"}>
                        {img ? (
                          <img
                            src={img}
                            alt={l.title}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                            No photo
                          </div>
                        )}
                      </div>
                    </Link>
                    <CardContent className={tight ? "space-y-1.5 p-3" : compact ? "space-y-2 p-3" : "space-y-3 p-4"}>
                      <div className="flex items-center justify-between gap-2">
                        <Badge variant="secondary" className={tight ? "text-[9px] px-1.5 py-0" : "text-[10px]"}>
                          {l.category}
                        </Badge>
                        {typeof l.ai_benchmark_pct === "number" && (
                          <span className={tight ? "inline-flex items-center gap-1 text-[9px] font-semibold text-accent" : "inline-flex items-center gap-1 text-[10px] font-semibold text-accent"}>
                            <Sparkles className={tight ? "h-2.5 w-2.5" : "h-3 w-3"} />
                            AI {l.ai_benchmark_pct > 0 ? "+" : ""}
                            {l.ai_benchmark_pct.toFixed(1)}%
                          </span>
                        )}
                      </div>
                      <h3 className={tight ? "line-clamp-2 text-xs font-bold leading-snug" : "line-clamp-2 text-sm font-bold leading-snug"}>
                        {l.title}
                      </h3>
                      <div className="flex items-baseline justify-between">
                        <div>
                          <div className={tight ? "text-sm font-bold text-foreground" : "text-lg font-bold text-foreground"}>
                            {formatINR(l.price_per_ton)}
                          </div>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            per tonne
                          </div>
                        </div>
                        <div className={tight ? "text-right text-[10px] text-muted-foreground" : "text-right text-xs text-muted-foreground"}>
                          {l.tonnage} T
                        </div>
                      </div>
                      <div className={tight ? "flex items-center gap-1 text-[10px] text-muted-foreground" : "flex items-center gap-1 text-xs text-muted-foreground"}>
                        <MapPin className={tight ? "h-2.5 w-2.5" : "h-3 w-3"} />
                        {l.hub}
                      </div>
                      <Button asChild variant={tight ? "outline" : "default"} size={tight ? "sm" : "sm"} className="w-full">
                        <Link to="/buyer">View on Foundry Connect</Link>
                      </Button>
                    </CardContent>
                  </Card>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  );
};

export default LiveListingsShowcase;
